import { useEffect, useState } from "react";
import { Plus, Pencil, X, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useGtt, conditionLabel } from "../lib/gtt";
import { useMarket } from "../lib/store";
import { useSubscribe } from "../lib/socket";
import { fmt, istDateTime } from "../lib/format";
import { Panel, PageHeader, th, td, btnGhost, Empty } from "../components/Panel";
import { SkeletonRows } from "../components/motion";

const STATUS_TONE = { ACTIVE: "text-primary border-primary/40", TRIGGERED: "text-up border-up/40", CANCELLED: "text-muted-foreground border-border", EXPIRED: "text-muted-foreground border-border" };
const Badge = ({ s, testid }) => <span data-testid={testid} className={`text-[9.5px] font-bold tracking-wider border rounded px-1.5 py-0.5 ${STATUS_TONE[s] || ""}`}>{s}</span>;

function useList(path, scope, version) {
  const [rows, setRows] = useState(null);
  const pv = useMarket((s) => s.portfolioVersion);
  useEffect(() => { api.get(path, { params: { status: scope } }).then((r) => setRows(r.data)).catch(() => setRows([])); }, [path, scope, version, pv]);
  return rows;
}

function Ltp({ k }) {
  const t = useMarket((s) => s.ticks[k]);
  return <span className="num">{fmt(t?.ltp)}</span>;
}

async function remove(path, label, bump) {
  try { await api.delete(path); toast.message(`Simulated order · ${label} cancelled`); bump(); } catch (e) { toast.error(errMsg(e)); }
}

function GttTable({ rows }) {
  const { openGtt, bump } = useGtt();
  if (!rows) return <SkeletonRows cols={8} testid="gtt-skeleton" />;
  if (!rows.length) return <Empty testid="gtt-empty">No GTT orders. Create one to auto-place a target or stop-loss order.</Empty>;
  return (
    <table className="w-full" data-testid="gtt-table">
      <thead className="border-b border-border"><tr>{["Created", "Instrument", "Type", "Side", "Qty", "Triggers (limit)", "LTP", "Status", ""].map((h, i) => <th key={i} className={th}>{h}</th>)}</tr></thead>
      <tbody>{rows.map((g) => (
        <tr key={g.id} className="border-b border-border/60 hover:bg-accent/40" data-testid={`gtt-row-${g.id}`}>
          <td className={`${td} num text-muted-foreground`}>{istDateTime(g.created_at)}</td>
          <td className={`${td} font-medium`}>{g.symbol}<span className="ml-1 text-[10px] text-muted-foreground">{g.exchange}</span></td>
          <td className={td}>{g.type === "oco" ? "OCO" : "Single"} · {g.product}</td>
          <td className={`${td} font-semibold ${g.side === "BUY" ? "text-up" : "text-down"}`}>{g.side}</td>
          <td className={`${td} num`}>{g.quantity}</td>
          <td className={`${td} num`}>{g.legs.map((l, i) => <div key={i} className={`text-[11.5px] ${g.triggered_leg === i ? "text-up font-semibold" : ""}`}>{l.label ? `${l.label} ` : ""}{fmt(l.trigger_price)} <span className="text-muted-foreground">({fmt(l.price)})</span></div>)}</td>
          <td className={td}><Ltp k={g.instrument_key} /></td>
          <td className={td}><Badge s={g.status} testid={`gtt-status-${g.id}`} />{g.order_status && <div className="text-[10px] text-muted-foreground mt-0.5" title={g.order_reason || ""}>Order {g.order_status}</div>}</td>
          <td className={`${td} text-right`}>{g.status === "ACTIVE" && <span className="flex justify-end gap-1">
            <button data-testid={`gtt-edit-${g.id}`} className={btnGhost} onClick={() => openGtt({ edit: g })}><Pencil className="h-3 w-3" /></button>
            <button data-testid={`gtt-cancel-${g.id}`} className={`${btnGhost} text-down`} onClick={() => remove(`/gtt/${g.id}`, "GTT", bump)}><X className="h-3 w-3" /></button></span>}</td>
        </tr>))}</tbody>
    </table>
  );
}

function AlertTable({ rows }) {
  const { bump } = useGtt();
  if (!rows) return <SkeletonRows cols={6} testid="alerts-skeleton" />;
  if (!rows.length) return <Empty testid="alerts-empty">No price alerts yet.</Empty>;
  return (
    <table className="w-full" data-testid="alerts-table">
      <thead className="border-b border-border"><tr>{["Created", "Instrument", "Condition", "LTP", "Note", "Status", ""].map((h, i) => <th key={i} className={th}>{h}</th>)}</tr></thead>
      <tbody>{rows.map((a) => (
        <tr key={a.id} className="border-b border-border/60 hover:bg-accent/40" data-testid={`alert-row-${a.id}`}>
          <td className={`${td} num text-muted-foreground`}>{istDateTime(a.created_at)}</td>
          <td className={`${td} font-medium`}>{a.symbol}<span className="ml-1 text-[10px] text-muted-foreground">{a.exchange}</span></td>
          <td className={`${td} num`}>{conditionLabel(a)}{a.email && <Mail className="inline h-3 w-3 ml-1.5 text-muted-foreground" />}</td>
          <td className={td}><Ltp k={a.instrument_key} /></td>
          <td className={`${td} text-muted-foreground max-w-[220px] truncate`}>{a.note || "—"}</td>
          <td className={td}><Badge s={a.status} testid={`alert-status-${a.id}`} />{a.triggered_ltp && <div className="text-[10px] text-muted-foreground mt-0.5 num">at ₹{fmt(a.triggered_ltp)} · {istDateTime(a.triggered_at)}</div>}</td>
          <td className={`${td} text-right`}>{a.status === "ACTIVE" && <button data-testid={`alert-cancel-${a.id}`} className={`${btnGhost} text-down`} onClick={() => remove(`/alerts/${a.id}`, "Alert", bump)}><X className="h-3 w-3" /></button>}</td>
        </tr>))}</tbody>
    </table>
  );
}

export default function GttAlerts() {
  const [tab, setTab] = useState("gtt");
  const [scope, setScope] = useState("active");
  const { openGtt, openAlert, version } = useGtt();
  const gtts = useList("/gtt", scope, version);
  const alerts = useList("/alerts", scope, version);
  useSubscribe([...(gtts || []), ...(alerts || [])].filter((x) => x.status === "ACTIVE").map((x) => x.instrument_key));
  const tabCls = (on) => `h-8 px-3 text-[12.5px] border-b-2 transition-colors duration-150 ${on ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`;
  return (
    <div className="p-4 lg:p-6" data-testid="gtt-page">
      <PageHeader title="GTT & Alerts">
        <button data-testid="new-gtt-button" onClick={() => openGtt()} className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-[12px] font-semibold flex items-center gap-1 hover:brightness-110"><Plus className="h-3.5 w-3.5" />New GTT</button>
        <button data-testid="new-alert-button" onClick={() => openAlert()} className="h-8 px-3 rounded-md border border-border text-[12px] font-semibold flex items-center gap-1 hover:bg-accent"><Plus className="h-3.5 w-3.5" />New alert</button>
      </PageHeader>
      <Panel>
        <div className="flex items-center gap-1 px-3 border-b border-border">
          <button data-testid="gtt-tab-gtt" className={tabCls(tab === "gtt")} onClick={() => setTab("gtt")}>GTT ({gtts?.filter((g) => g.status === "ACTIVE").length ?? 0})</button>
          <button data-testid="gtt-tab-alerts" className={tabCls(tab === "alerts")} onClick={() => setTab("alerts")}>Alerts ({alerts?.filter((a) => a.status === "ACTIVE").length ?? 0})</button>
          <span className="flex-1" />
          {[["active", "Active"], ["all", "History"]].map(([v, l]) => <button key={v} data-testid={`gtt-scope-${v}`} onClick={() => setScope(v)} className={`${btnGhost} ${scope === v ? "border-primary text-primary" : ""}`}>{l}</button>)}
        </div>
        <div className="overflow-auto thin-scroll">{tab === "gtt" ? <GttTable rows={gtts} /> : <AlertTable rows={alerts} />}</div>
      </Panel>
    </div>
  );
}
